import React from 'react';
import { topVenues2025 } from '../../data/topLists';
import { venuesData } from '../../data/venuesData';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { translations as en } from '../../translations/en';
import { translations as es } from '../../translations/es';

const translations = { en, es };

export default function TopVenues() {
  const { language } = useLanguage();
  const t = translations[language];
  const venues = topVenues2025.map(id => venuesData[id]);

  // Map of locations to their destination guide paths
  const destinationPaths = {
    "cancun-tulum-riviera-maya": "/destinations/cancun-tulum-riviera-maya",
    "los-cabos": "/destinations/los-cabos",
    "mexico-city": "/destinations/mexico-city",
    "san-miguel-de-allende": "/destinations/san-miguel-de-allende"
  };

  // Format location for display
  const formatLocation = (location) => {
    return location.split('-').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  return (
    <div className="font-inter bg-white text-[#181c24] min-h-screen">
      {/* Hero Section */}
      <div className="relative h-[42vh] bg-cover bg-center" style={{ backgroundImage: "url('/images/beach_seating.jpg')" }}>
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative h-full flex items-center justify-center">
          <h1 className="text-4xl md:text-5xl font-playfair text-white text-center px-4">
            {t.resources.topVenuesHeroTitle}
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-20 py-12">
        <div className="flex flex-col md:flex-row gap-8">
          {/* Main Content */}
          <article className="md:w-2/3">
            <div className="prose prose-lg max-w-none">
              <p className="mb-8">
                {t.resources.topVenuesIntro1}
              </p>
              <p className="mb-12">
                {t.resources.topVenuesIntro2}
              </p>
            </div>

            <ul className="space-y-16">
              {venues.map((venue, index) => (
                <li key={venue.id} className="mb-12">
                  <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                    <Link 
                      to={venue.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="block"
                    >
                      <img 
                        src={venue.imageUrl} 
                        alt={language === 'en' ? venue.name : venue.name_es} 
                        className="w-full h-64 object-cover"
                      />
                      <div className="p-6">
                        <h3 className="text-2xl font-playfair mb-3">{index + 1}. {language === 'en' ? venue.name : venue.name_es}</h3>
                      </div>
                    </Link>
                    <div className="px-6 pb-6">
                      <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                        <Link 
                          to={destinationPaths[venue.location]} 
                          className="text-[#bfa76a] hover:text-[#a68d5d] font-medium hover:underline"
                        >
                          {formatLocation(venue.location)}
                        </Link>
                      </div>
                      <p className="mb-2 font-semibold text-gray-800">{t.resources.whyWeLoveIt}</p>
                      <p className="mb-6 text-gray-700 leading-relaxed">{language === 'en' ? venue.description : venue.description_es}</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {[...(venue.features.primary || []), ...(venue.features.amenities || []), ...(venue.features.settings || [])].map((feature, i) => (
                          <span key={i} className="px-3 py-1 bg-[#bfa76a]/10 text-[#bfa76a] rounded-full text-sm">
                            {feature}
                          </span>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span className="font-semibold">{t.resources.ratingLabel}</span>
                        <span>{venue.rating}</span>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <div className="prose prose-lg max-w-none mt-12">
              <p>
                {t.resources.topVenuesOutro}
              </p>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="md:w-1/4 max-w-xs">
            <div className="sticky top-32 space-y-6">
              {/* About Assemblage */}
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-base font-playfair mb-3">{t.resources.aboutAssemblageTitle}</h3>
                <p className="text-xs text-gray-700 mb-3">
                  {t.resources.aboutAssemblageDesc}
                </p>
                <Link
                  to="/about"
                  className="text-[#bfa76a] hover:text-[#a68d5d] font-semibold inline-flex items-center text-xs"
                >
                  {t.resources.learnMore}
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </div>

              {/* Schedule Consultation */}
              <div className="bg-[#181c24] p-6 rounded-lg shadow-md text-white">
                <h3 className="text-base font-playfair mb-3">{t.resources.readyToFindVenue}</h3>
                <p className="mb-4 text-xs text-gray-300">
                  {t.resources.ourPlannersHelp}
                </p>
                <Link
                  to="/contact"
                  className="block w-full text-center bg-[#bfa76a] text-white py-3 px-6 rounded-full font-semibold hover:bg-[#a68d5d] transition-colors text-xs"
                >
                  {t.resources.scheduleConsultation}
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
// Note: Add the needed translation keys to en.js and es.js under a 'resources' section. 